import React from "react";
import { emptyOverride, hasCouponBenefit, upfrontBenefits } from "./draftModel";
import { Messages, NumberField, Section } from "./fields";
import ProductGroupField from "./ProductGroupField";

const OverridesSection = ({ form, setForm, msgs, locked, productGroups }) => {
  const upfront = upfrontBenefits(form);
  const tenures = form.tenures.filter((tenure) => tenure !== "");
  const couponBenefit = hasCouponBenefit(form);

  const patch = (index, changes) =>
    setForm((prev) => ({
      ...prev,
      overrides: prev.overrides.map((override, i) => (i === index ? { ...override, ...changes } : override)),
    }));
  const patchMap = (index, mapName, key, value) =>
    setForm((prev) => ({
      ...prev,
      overrides: prev.overrides.map((override, i) =>
        i === index ? { ...override, [mapName]: { ...override[mapName], [key]: value } } : override
      ),
    }));

  return (
    <Section
      id="overrides"
      title="Overrides"
      description="Fixed prices for one product group. Anything left blank is priced by the rule above."
      messages={msgs("overrides")}
    >
      {form.overrides.length === 0 && <p className="warranty-help">No overrides.</p>}
      {form.overrides.map((override, index) => {
        const at = (field) => `overrides.${index}.${field}`;
        const id = (field) => `warranty-override-${index}-${field}`;
        return (
          <div className="warranty-subcard" key={index}>
            <div className="warranty-subcard-head">
              <strong>{override.productGroup || `Override ${index + 1}`}</strong>
              <button
                type="button"
                className="warranty-inline-btn is-danger"
                disabled={locked}
                aria-label={`Remove override ${override.productGroup || index + 1}`}
                onClick={() =>
                  setForm((prev) => ({ ...prev, overrides: prev.overrides.filter((_, i) => i !== index) }))
                }
              >
                Remove
              </button>
            </div>
            <Messages messages={[...msgs(`overrides.${index}`), ...msgs(at("prices"))]} />
            <div className="row">
              <div className="col-md-6">
                <ProductGroupField
                  id={id("group")}
                  label="Product group"
                  value={override.productGroup}
                  groups={productGroups}
                  takenNames={form.overrides
                    .filter((_, i) => i !== index)
                    .map((other) => other.productGroup)
                    .filter((name) => name !== "")}
                  disabled={locked}
                  messages={msgs(at("productGroup"))}
                  onChange={(value) => patch(index, { productGroup: value })}
                />
              </div>
              <div className="col-md-3">
                <NumberField
                  id={id("oneTime")}
                  label="One-time price"
                  kind="rupees"
                  value={override.oneTime}
                  disabled={locked}
                  messages={msgs(at("prices.oneTimeMinor"))}
                  onChange={(value) => patch(index, { oneTime: value })}
                />
              </div>
              {couponBenefit && (
                <div className="col-md-3">
                  <NumberField
                    id={id("coupon")}
                    label="Coupon value"
                    kind="rupees"
                    value={override.couponValue}
                    disabled={locked}
                    messages={msgs(at("prices.couponValueMinor"))}
                    onChange={(value) => patch(index, { couponValue: value })}
                  />
                </div>
              )}
              {tenures.map((tenure) => (
                <div className="col-md-3" key={`m-${tenure}`}>
                  <NumberField
                    id={id(`monthly-${tenure}`)}
                    label={`${tenure} months instalment`}
                    kind="rupees"
                    value={override.monthly[tenure] === undefined ? "" : override.monthly[tenure]}
                    disabled={locked}
                    messages={msgs(at(`prices.monthly.${tenure}`))}
                    onChange={(value) => patchMap(index, "monthly", tenure, value)}
                  />
                </div>
              ))}
              {upfront.map((benefit) => (
                <div className="col-md-3" key={`a-${benefit.key}`}>
                  <NumberField
                    id={id(`addon-${benefit.key}`)}
                    label={`${benefit.label || benefit.key} price`}
                    kind="rupees"
                    value={override.addons[benefit.key] === undefined ? "" : override.addons[benefit.key]}
                    disabled={locked}
                    messages={msgs(at(`prices.addons.${benefit.key}`))}
                    onChange={(value) => patchMap(index, "addons", benefit.key, value)}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <button
        type="button"
        className="warranty-inline-btn is-primary"
        disabled={locked}
        onClick={() => setForm((prev) => ({ ...prev, overrides: [...prev.overrides, emptyOverride()] }))}
      >
        Add override
      </button>
    </Section>
  );
};

export default OverridesSection;
